import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Plug, Play, Download, CheckCircle2, XCircle, Loader2 } from "lucide-react";

function StatusPill({ row }) {
  const ok = row.ok;
  const warn = [401, 403].includes(row.status);
  const cls = ok ? "bg-[#E7F0E3] text-[#3C5A2E]" : warn ? "bg-[#FDF0DC] text-[#8A5A1A]" : "bg-[#FBE4E4] text-[#8A1F1F]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {row.status || "—"}
    </span>
  );
}

function ResultTable({ title, meta, rows }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`pms-table-${title}`}>
      <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-medium text-[#45423C]">{title}</h2>
        <span className="text-xs text-[#6F6A60]">
          {meta?.base}{meta?.auth_method ? ` · auth: ${meta.auth_method}` : ""}
          {meta && !meta.configured ? " · δεν έχει ρυθμιστεί" : ""}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
              <th className="px-4 py-2.5">Endpoint</th><th className="px-4 py-2.5">Κατάσταση</th>
              <th className="px-4 py-2.5 text-right">Εγγραφές</th><th className="px-4 py-2.5">Μήνυμα</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((row, i) => (
              <tr key={i} className="border-t border-[#E3DED4] align-top" data-testid={`pms-row-${title}-${i}`}>
                <td className="px-4 py-2 font-medium text-[#45423C]">{row.endpoint}
                  <div className="text-[10px] text-[#9A948A] font-mono">{row.path}</div>
                </td>
                <td className="px-4 py-2"><StatusPill row={row} /></td>
                <td className="px-4 py-2 text-right tabular">{row.ok ? row.count : "—"}</td>
                <td className="px-4 py-2 text-[#6F6A60] text-xs max-w-[320px]">
                  {row.message}
                  {row.sample && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[#C86235]">δείγμα (μασκαρισμένο)</summary>
                      <pre className="mt-1 text-[10px] bg-[#F7F5F1] p-2 rounded-lg overflow-x-auto max-h-48">{JSON.stringify(row.sample, null, 1)}</pre>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Integrations() {
  const [data, setData] = useState(null);

  const run = useMutation({
    mutationFn: async () => (await api.get("/pms/diagnostics")).data,
    onSuccess: (d) => { setData(d); toast.success("Το διαγνωστικό ολοκληρώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const download = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `hotelizer_diagnostics_${data?.generated_at || "report"}.json`;
    a.click();
  };

  const hzOk = data?.hotelizer?.rows?.filter((r) => r.ok).length || 0;
  const hzTotal = data?.hotelizer?.rows?.length || 0;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">PMS</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 flex items-center gap-2">
            <Plug className="h-7 w-7 text-[#C86235]" /> Διαγνωστικό Σύνδεσης
          </h1>
          <p className="text-[#6F6A60] text-sm mt-1 max-w-2xl">
            Read-only έλεγχος σύνδεσης με Hotelizer & WebHotelier. Δεν γίνεται καμία εγγραφή/χρέωση.
            Τα στοιχεία επισκεπτών εμφανίζονται μασκαρισμένα.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <button data-testid="pms-download" onClick={download}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
              <Download className="h-4 w-4" /> Λήψη αναφοράς
            </button>
          )}
          <button data-testid="pms-run" onClick={() => run.mutate()} disabled={run.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            {run.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Εκτέλεση διαγνωστικού
          </button>
        </div>
      </div>

      {!data && !run.isPending && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-8 text-center text-[#6F6A60]" data-testid="pms-empty">
          Πάτησε «Εκτέλεση διαγνωστικού» για να ελέγξουμε τη σύνδεση και να δούμε δείγμα από κρατήσεις, επισκέπτες, δωμάτια κ.λπ.
        </div>
      )}

      {data && (
        <>
          <div className="rounded-2xl bg-[#45423C] text-white px-5 py-4 flex flex-wrap items-center gap-4" data-testid="pms-summary">
            <span className="text-sm">Hotelizer: <strong className="tabular">{hzOk}/{hzTotal}</strong> endpoints OK</span>
            <span className="text-xs text-white/70">{data.generated_at}</span>
          </div>
          <ResultTable title="Hotelizer" meta={data.hotelizer} rows={data.hotelizer?.rows} />
          <ResultTable title="WebHotelier" meta={data.webhotelier} rows={data.webhotelier?.rows} />
        </>
      )}
    </div>
  );
}
